import React from "react";
import { Map } from "immutable";
import { PropertyPanel as Panel } from "@showitinc/showit-wc";

import AppConstants from "../../constants/AppConstants";
import PageProperties from "./page/PageProperties";
import BlockProperties from "./block/BlockProperties";
import ElementProperties from "../../components/ElementProperties";
import ElementsProperties from "./element/ElementsProperties";
import { recursivelyCopySyncedPropsToMobile } from "../../utils/SyncUtils";
import CanvasSetProperties from "../../components/CanvasSetProperties";
import CanvasSetSiteCanvasProperties from "../../components/CanvasSetSiteCanvasProperties";
import { MapElementProperties } from "@/utils/Element/ElementPropertyMapper";
import BlockUtils from "@/utils/BlockUtils";
import ElementUtils from "@/utils/ElementUtils";
import "./Panel.css";

const { SITE_ITEM_PAGE } = AppConstants;
const PROPERTY_PANEL_WIDTH = "15.625rem";

const PropertiesPanel = ({ blockContainer, viewState }) => {
  let view = null;

  const pagePropertiesPanel = viewState.get("pagePropertiesPanel");
  const blockPropertiesPanel = viewState.get("blockPropertiesPanel");
  const elementPropertiesPanel = viewState.get("elementPropertiesPanel");
  const elementsPropertiesPanel = viewState.get("elementsPropertiesPanel");

  const containerType = blockContainer.get("type");
  const block = blockContainer.get("selectedBlock");
  const container = blockContainer.get("data");
  const selectedLayout = blockContainer.get("layout");
  const [theme] = recursivelyCopySyncedPropsToMobile(blockContainer.get("theme"));
  const isWP = blockContainer.get("isWP");
  const isCanvasSet = blockContainer.get("isCanvasSet");
  const isPartOfCanvasSet = block.get("canvasSet");

  if (!block.isEmpty() && !isPartOfCanvasSet) {
    const selectedElements = block.getIn(["selected", "elements"]);

    const mappedElements = selectedElements?.map((elem) =>
      MapElementProperties(
        BlockUtils.getBlockElement(block, elem?.id),
        elem.id,
        theme,
        selectedLayout,
      ),
    );

    if (selectedElements?.size === 1) {
      const firstElem = selectedElements.first();
      const element = ElementUtils.getSelectedElements(block)?.[0];

      // Dont try to render ElementProperties if the element is not valid
      if (element) {
        view = (
          <ElementProperties
            block={block}
            element={element}
            elementId={firstElem.id}
            isWP={isWP}
            layout={selectedLayout}
            mappedElement={mappedElements.first()}
            selectedElements={selectedElements}
            theme={theme}
            viewState={elementPropertiesPanel}
          />
        );
      }
    } else if (selectedElements?.size > 1) {
      view = (
        <ElementsProperties
          block={block}
          isWP={isWP}
          layout={selectedLayout}
          mappedElements={mappedElements}
          selectedElements={selectedElements}
          theme={theme}
          viewState={elementsPropertiesPanel}
        />
      );
    }

    // If no view was set (either no elements or a single element was invalid), show BlockProperties
    if (!view) {
      view = (
        <BlockProperties
          viewState={blockPropertiesPanel}
          block={block}
          layout={selectedLayout}
          theme={theme}
          isWP={isWP}
        />
      );
    }
  } else if (isPartOfCanvasSet) {
    view = (
      <CanvasSetSiteCanvasProperties
        blockContainer={blockContainer}
        containerType={containerType}
      />
    );
  } else if (containerType === SITE_ITEM_PAGE) {
    view = (
      <PageProperties
        page={container}
        viewState={pagePropertiesPanel}
        layout={selectedLayout}
        theme={theme}
        blockContainer={blockContainer}
      />
    );
  } else if (isCanvasSet) {
    view = <CanvasSetProperties blockContainer={blockContainer} />;
  }

  const style = {
    width: PROPERTY_PANEL_WIDTH,
  };

  return (
    <Panel className="property-panel" style={style} data-intercom-target="panel properties">
      {view}
    </Panel>
  );
};

export default PropertiesPanel;
